import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionContext = createContext({
  tier: 'free',
  isAdmin: false,
  limits: {},
  usage: {},
  prices: {},
  loading: true,
  isPremium: false,
  showUpgradeModal: false,
  setShowUpgradeModal: () => {},
  upgradeMessage: '',
  setUpgradeMessage: () => {},
  checkLimit: async () => ({ allowed: true }),
  triggerUpgrade: () => {},
  refreshSubscription: () => {},
});

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [tier, setTier] = useState('free');
  const [isAdmin, setIsAdmin] = useState(false);
  const [limits, setLimits] = useState({});
  const [usage, setUsage] = useState({});
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');

  const isPremium = tier === 'monthly' || tier === 'yearly' || isAdmin;

  const fetchSubscription = useCallback(async () => {
    const token = localStorage.getItem('tapInToken');
    if (!token || !user?.id) { setLoading(false); return; }
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/my-subscription`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTier(data.tier);
        setIsAdmin(data.is_admin);
        setLimits(data.limits);
        setUsage(data.usage);
        setPrices(data.prices);
      }
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
    }
    setLoading(false);
  }, [user?.id]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const checkLimit = useCallback(async (action, playlistId) => {
    const token = localStorage.getItem('tapInToken');
    if (!token) return { allowed: true };
    try {
      const url = `${API_URL}/api/subscriptions/check-limit/${action}${playlistId ? `?playlist_id=${playlistId}` : ''}`;
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) return await res.json();
      return { allowed: true };
    } catch {
      return { allowed: true };
    }
  }, []);

  const triggerUpgrade = useCallback((message) => {
    setUpgradeMessage(message || "Upgrade to Tap In Premium for unlimited access.");
    setShowUpgradeModal(true);
  }, []);

  return (
    <SubscriptionContext.Provider value={{
      tier, isAdmin, limits, usage, prices, loading, isPremium,
      showUpgradeModal, setShowUpgradeModal,
      upgradeMessage, setUpgradeMessage,
      checkLimit, triggerUpgrade, refreshSubscription: fetchSubscription,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => useContext(SubscriptionContext);
